import { Navigate, Route } from "react-router-dom";

import { queryClient } from "@/providers/QueryClient";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { featureFlagRoute } from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";

import { CreateDataset } from "./CreateDataset";
import { CreateDatasetEvent } from "./CreateDatasetEvent";
import { Dataset } from "./Dataset";
import { DatasetAgentThreads } from "./Dataset/DatasetAgentThreads";
import { DatasetDetails } from "./Dataset/DatasetDetails";
import { DatasetEvents } from "./Dataset/DatasetEvents";
import { DatasetFileBrowser } from "./Dataset/DatasetFileBrowser";
import { DatasetInvocations } from "./Dataset/DatasetInvocations";
import { DatasetPermissions } from "./Dataset/DatasetPermissions";
import { DatasetSessions } from "./Dataset/DatasetSessions";
import { DatasetAISummary } from "./Dataset/DatasetSummary";
import { DatasetTopics } from "./Dataset/DatasetTopics";
import { DatasetsList } from "./DatasetsList";
import { loadDatasetByName } from "./loaders";

export const DatasetsRoutes = () => [
  <Route
    key={"/"}
    index
    loader={loadSearchTablePreferences(
      queryClient,
      "dataset_search_table_preferences",
    )}
    element={
      <SystemUserOnly fallback={<Navigate to="/search" replace />}>
        <DatasetsList />
      </SystemUserOnly>
    }
  />,
  <Route key={"create"} path="create" element={<CreateDataset />} />,
  <Route
    key={"name"}
    path="name/:datasetName"
    loader={loadDatasetByName}
    shouldRevalidate={({ currentUrl, nextUrl }) => {
      return currentUrl.pathname !== nextUrl.pathname;
    }}
    // If the loader does not redirect to a dataset,
    // take the user to the dataset search page.
    element={<Navigate to="/search" replace />}
  />,
  <Route
    key={"id/events/create"}
    path=":datasetPath/events/create"
    element={<CreateDatasetEvent />}
  />,
  // Dataset detail view: parent layout with one child route per tab,
  // mounted via <Dataset />'s <Outlet>. <Dataset /> publishes the
  // dataset record, current org, and datasetId through outlet context,
  // which tabs read via `useDatasetContext` instead of re-deriving from
  // useParams/useAuth.
  <Route key={"id"} path=":datasetPath" element={<Dataset />}>
    <Route index element={<Navigate to="files" replace />} />
    <Route path="files/*" element={<DatasetFileBrowser />} />
    {/*
        Opening the Sessions tab must have saved column order / page size
        in cache before <DatasetSessions /> mounts: `useResourceView`
        snapshots `preferences` once via `React.useState` and never
        re-reads. `featureFlagRoute` already uses its loader slot for the
        flag gate, so this pathless wrapper Route runs the preferences
        loader; <InheritOutletContext /> forwards <Dataset />'s outlet
        context through to the gated child.
    */}
    <Route
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<InheritOutletContext />}
    >
      {featureFlagRoute({
        path: "sessions",
        flagName: "release-sessions",
        redirectTo: ({ params }) => `/datasets/${params.datasetPath}/files`,
        element: <DatasetSessions />,
      })}
    </Route>
    <Route path="topics" element={<DatasetTopics />} />
    <Route path="events" element={<DatasetEvents />} />
    <Route path="actions" element={<DatasetInvocations />} />
    <Route path="agent-threads" element={<DatasetAgentThreads />} />
    <Route path="details" element={<DatasetDetails />} />
    <Route path="permissions" element={<DatasetPermissions />} />
    <Route path="summary" element={<DatasetAISummary />} />
    <Route path="*" element={<Navigate to="files" replace />} />
  </Route>,
];
